import usePostMessage from '@/common/composables/usePostMessage';
import checkCookieAvailability from '@/common/utils/checkCookieAvailability';

export default async function checkTopLevelStorageAccess(
  onSetAccess: (hasAccess: boolean) => void
): Promise<boolean> {
  if (window
    && window.top !== window
    && 'hasStorageAccess' in document
    && 'requestStorageAccess' in document
    // Chrome's userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131.0.0.0 Safari/537.36'
    && (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome'))
  ) {
    const hasAccess = checkCookieAvailability() || await document.hasStorageAccess();
    onSetAccess(hasAccess);

    if (!hasAccess) {
      const { attachPostMessageListener, sendPostMessage } = usePostMessage();

      console.log('No storage access. Waiting for event=requestStorageAccess from top-level application.');
      sendPostMessage('storageAccess', { state: 'wait' });

      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          // Page render must not be blocked
          resolve(false);
        }, 1000);

        const storageAccessRequest = async () => {
          clearTimeout(timeout);
          try {
            await document.requestStorageAccess();

            document.removeEventListener('click', storageAccessRequest);
            document.removeEventListener('keypress', storageAccessRequest);
            sendPostMessage('storageAccess', { state: 'success' });

            onSetAccess(true);
            resolve(true);
          } catch (e) {
            console.error('Failed granting storage access:', e);

            sendPostMessage(
              'storageAccess',
              {
                state: 'error',
                reason: e,
                message: 'emit a postMessage("requestStorageAccess") as a reaction on click event',
                href: 'https://developer.mozilla.org/en-US/docs/Web/API/Document/requestStorageAccess'
              }
            );

            resolve(false);
          }
        };

        document.addEventListener('click', storageAccessRequest);
        document.addEventListener('keypress', storageAccessRequest);

        attachPostMessageListener(async (event) => {
          if (event.data === 'requestStorageAccess') {
            await storageAccessRequest();
          }
        });
      });
    }
  }

  return Promise.resolve(true);
}
